import { DetailedOrder } from '../domain/types';
import { COLLECTIONS } from '../domain/collections';
import { db } from '../infrastructure/firebaseConfig';
import { logger } from '../infrastructure/logger';
import { PAGINATION, REPOSITORIES } from '../domain/constants';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  where,
  QueryConstraint,
  QueryDocumentSnapshot
} from "firebase/firestore";
import { BaseRepository, BaseEntity } from '../domain/services/BaseRepository';
import { createPollingService } from '../infrastructure/pollingService';

type OrderListener = (orders: DetailedOrder[]) => void;

interface OrderEntity extends DetailedOrder, BaseEntity {}

class OrderQueries extends BaseRepository<OrderEntity> {
  protected collectionName = COLLECTIONS.ORDERS;

  protected toEntity(doc: QueryDocumentSnapshot): OrderEntity {
    const data = doc.data() as DetailedOrder;
    return {
      ...data,
      id: doc.id,
      createdAt: data.date || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  }

  // --- Polling Logic con PollingService ---
  startPolling(partnerId: string | null, isAdmin: boolean, callback: OrderListener, intervalMs: number = REPOSITORIES.POLLING_INTERVAL_MS): () => void {
    const pollingService = createPollingService(intervalMs, {
      maxConsecutiveErrors: 5,
      baseDelay: 2000,
      maxDelay: 30000,
      onError: (error, count) => {
        if (count <= 5) {
          logger.error(`[OrderQueries] Polling error ${count}/5`, error);
        }
      },
      onCircuitOpen: () => {
        logger.error('[OrderQueries] Circuit breaker opened - stopping order polling', new Error('Circuit breaker opened'));
      },
      onCircuitClose: () => {
        logger.info('[OrderQueries] Circuit breaker closed - resuming order polling');
      }
    });

    return pollingService.start(
      async () => {
        let q;
        if (isAdmin || !partnerId) {
          q = query(collection(db, this.collectionName), orderBy("createdAt", "desc"), limit(PAGINATION.ADMIN_ORDERS_LIMIT));
        } else {
          q = query(
            collection(db, this.collectionName),
            where("partnerId", "==", partnerId),
            orderBy("createdAt", "desc"),
            limit(PAGINATION.PARTNER_ORDERS_LIMIT)
          );
        }
        const snapshot = await getDocs(q);
        const orders = snapshot.docs.map((doc) => {
          const data = doc.data();
          if (!data) return null;
          return { ...data, id: doc.id } as DetailedOrder;
        }).filter((order): order is DetailedOrder => order !== null);
        return orders;
      },
      callback,
      (error) => {
        callback([]);
      }
    );
  }

  // Rate-limited error logging (log 1 error per minute max)
  private lastErrorTime: number = 0;
  private errorCount: number = 0;
  private readonly RATE_LIMIT_MS = 60000; // 1 minute

  private shouldLogError(): boolean {
    const now = Date.now();
    if (now - this.lastErrorTime > this.RATE_LIMIT_MS) {
      this.lastErrorTime = now;
      this.errorCount = 0;
    }
    return this.errorCount < 5; // Log max 5 errors per minute
  }

  private logError(message: string, error: unknown): void {
    if (this.shouldLogError()) {
      const errorToLog = error instanceof Error ? error : new Error(String(error));
      logger.error(message, errorToLog);
      this.errorCount++;
    }
  }

  // --- Query Methods ---
  async getMyOrders(userId?: string, email?: string, dni?: string, lastVisible: DocumentSnapshot | null = null, pageSize: number = PAGINATION.USER_ORDERS_LIMIT): Promise<{ orders: DetailedOrder[], lastDoc: DocumentSnapshot | null }> {
    try {
      if (!userId && !email && !dni) return { orders: [], lastDoc: null };

      const constraints: QueryConstraint[] = [
        orderBy("createdAt", "desc"),
        limit(pageSize)
      ];
      if (lastVisible) constraints.push(startAfter(lastVisible));

      let q;
      if (userId && typeof userId === 'string' && userId !== 'undefined') {
        q = query(collection(db, this.collectionName), where("userId", "==", userId), ...constraints);
      } else if (email && typeof email === 'string' && email !== 'undefined') {
        q = query(collection(db, this.collectionName), where("email", "==", email), ...constraints);
      } else if (dni && typeof dni === 'string' && dni !== 'undefined') {
        q = query(collection(db, this.collectionName), where("guestInfo.dni", "==", dni), ...constraints);
      } else {
        return { orders: [], lastDoc: null };
      }

      const snapshot = await getDocs(q);
      const orders = snapshot.docs.map(doc => ({ ...(doc.data() as object), id: doc.id } as DetailedOrder));
      const lastDoc = (snapshot.docs[snapshot.docs.length - 1] as DocumentSnapshot | undefined) || null;

      // Reset error count on success
      this.errorCount = 0;

      return { orders, lastDoc: lastDoc as DocumentSnapshot | null };
    } catch (error) {
      this.logError('[OrderQueries] Error fetching my orders', error);
      return { orders: [], lastDoc: null };
    }
  }

  async getOrderById(id: string): Promise<DetailedOrder | undefined> {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          ...data,
          id: docSnap.id,
          items: data.items || [],
          statusLabel: data.statusLabel || 'Pendiente',
          timeline: data.timeline || []
        } as DetailedOrder;
      }
      return undefined;
    } catch (error) {
      this.logError(`[OrderQueries] Error fetching order ${id}`, error);
      return undefined;
    }
  }

  async getAllOrders(
    lastVisible: DocumentSnapshot | null = null,
    pageSize: number = PAGINATION.DEFAULT_PAGE_SIZE,
    status?: string,
    isArchived: boolean = false,
    maxFetchAttempts: number = 5
  ): Promise<{ orders: DetailedOrder[], lastDoc: DocumentSnapshot | null, hasMore: boolean }> {
    try {
      const orders: DetailedOrder[] = [];
      let currentLastDoc = lastVisible;
      let fetchAttempts = 0;
      let totalFetched = 0;
      const BATCH_SIZE = pageSize * 3; // Fetch 3x to account for filtering

      // Keep fetching until we have enough orders or no more data
      while (orders.length < pageSize && fetchAttempts < maxFetchAttempts) {
        const constraints: QueryConstraint[] = [
          orderBy("createdAt", "desc"),
          limit(BATCH_SIZE)
        ];

        if (currentLastDoc) {
          constraints.push(startAfter(currentLastDoc));
        }

        const q = query(collection(db, this.collectionName), ...constraints);
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          break; // No more data
        }

        totalFetched += snapshot.docs.length;

        // Filter documents client-side
        const batchOrders = snapshot.docs
          .map(doc => ({ ...(doc.data() as object), id: doc.id } as DetailedOrder))
          .filter(order => {
            // Filter by isArchived
            if (order.isArchived !== isArchived) return false;
            // Filter by status if specified
            if (status && status !== 'all' && order.status !== status) return false;
            return true;
          });

        // Add to results
        orders.push(...batchOrders);

        // Update lastDoc to the last document of this batch
        currentLastDoc = snapshot.docs[snapshot.docs.length - 1];

        fetchAttempts++;

        // If we got a full batch but no valid orders, continue fetching
        // If we got fewer docs than BATCH_SIZE, we've reached the end
        if (snapshot.docs.length < BATCH_SIZE) {
          break;
        }
      }

      // Trim to requested page size
      const finalOrders = orders.slice(0, pageSize);

      // Determine if there are more results
      // We have more if: we filled the page AND there might be more data
      const hasMore = orders.length >= pageSize && fetchAttempts < maxFetchAttempts;

      // Reset error count on success
      this.errorCount = 0;

      logger.info(`[OrderQueries] Fetched ${finalOrders.length} orders (${totalFetched} total docs scanned, ${fetchAttempts} attempts)`);

      return { orders: finalOrders, lastDoc: currentLastDoc, hasMore };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      // Check if error is due to missing index
      if (err.message.includes('index') || (error as any)?.code === 'failed-precondition') {
        logger.error('[OrderQueries] Missing Firestore index. Consider creating composite index for orders collection', err);
      }

      // Use rate-limited error logging
      this.logError('[OrderQueries] Error fetching all orders', err);
      return { orders: [], lastDoc: null, hasMore: false };
    }
  }

  async getOrdersByPartner(
    partnerId: string,
    lastVisible: DocumentSnapshot | null = null,
    pageSize: number = PAGINATION.DEFAULT_PAGE_SIZE
  ): Promise<{ orders: DetailedOrder[], lastDoc: DocumentSnapshot | null, hasMore: boolean }> {
    try {
      const orders: DetailedOrder[] = [];
      let currentLastDoc = lastVisible;
      let fetchAttempts = 0;
      let totalFetched = 0;
      const BATCH_SIZE = pageSize * 2; // Fetch 2x to account for any filtering
      const MAX_FETCH_ATTEMPTS = 3;

      // Keep fetching until we have enough orders or no more data
      while (orders.length < pageSize && fetchAttempts < MAX_FETCH_ATTEMPTS) {
        const constraints: QueryConstraint[] = [
          where("partnerId", "==", partnerId),
          orderBy("createdAt", "desc"),
          limit(BATCH_SIZE)
        ];

        if (currentLastDoc) {
          constraints.push(startAfter(currentLastDoc));
        }

        const q = query(collection(db, this.collectionName), ...constraints);
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          break; // No more data
        }

        totalFetched += snapshot.docs.length;

        const batchOrders = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            ...data,
            id: doc.id,
            items: data.items || [],
            statusLabel: data.statusLabel || 'Pendiente',
            timeline: data.timeline || []
          } as DetailedOrder;
        });

        orders.push(...batchOrders);

        // Update cursor to last document of this batch
        currentLastDoc = snapshot.docs[snapshot.docs.length - 1];
        fetchAttempts++;

        // If we got fewer docs than BATCH_SIZE, we've reached the end
        if (snapshot.docs.length < BATCH_SIZE) {
          break;
        }
      }

      // Trim to requested page size
      const finalOrders = orders.slice(0, pageSize);

      // Has more if we filled the page and haven't reached the end
      const hasMore = orders.length >= pageSize && fetchAttempts < MAX_FETCH_ATTEMPTS;

      // Reset error count on success
      this.errorCount = 0;

      logger.info(`[OrderQueries] Fetched ${finalOrders.length} orders for partner ${partnerId.substring(0, 8)} (${totalFetched} docs scanned)`);

      return { orders: finalOrders, lastDoc: currentLastDoc, hasMore };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('index') || (error as any).code === 'failed-precondition') {
          logger.error('[OrderQueries] Missing Firestore index. Create: (partnerId ASC, createdAt DESC)', error);
        }
        this.logError('[OrderQueries] Error fetching orders by partner', error);
      } else {
        this.logError('[OrderQueries] Error fetching orders by partner', new Error('Unknown error'));
      }
      return { orders: [], lastDoc: null, hasMore: false };
    }
  }

  async getOrdersByPartnerAndStatus(
    partnerId: string,
    status: string,
    lastVisible: DocumentSnapshot | null = null,
    pageSize: number = PAGINATION.DEFAULT_PAGE_SIZE
  ): Promise<{ orders: DetailedOrder[], lastDoc: DocumentSnapshot | null, hasMore: boolean }> {
    try {
      const orders: DetailedOrder[] = [];
      let currentLastDoc = lastVisible;
      let fetchAttempts = 0;
      let totalFetched = 0;
      const BATCH_SIZE = pageSize * 2; // Fetch 2x to be safe
      const MAX_FETCH_ATTEMPTS = 3;

      // Keep fetching until we have enough orders or no more data
      while (orders.length < pageSize && fetchAttempts < MAX_FETCH_ATTEMPTS) {
        const constraints: QueryConstraint[] = [
          where("partnerId", "==", partnerId),
          where("status", "==", status),
          orderBy("createdAt", "desc"),
          limit(BATCH_SIZE)
        ];

        if (currentLastDoc) {
          constraints.push(startAfter(currentLastDoc));
        }

        const q = query(collection(db, this.collectionName), ...constraints);
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          break; // No more data
        }

        totalFetched += snapshot.docs.length;

        const batchOrders = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            ...data,
            id: doc.id,
            items: data.items || [],
            statusLabel: data.statusLabel || 'Pendiente',
            timeline: data.timeline || []
          } as DetailedOrder;
        });

        orders.push(...batchOrders);

        // Update cursor to last document of this batch
        currentLastDoc = snapshot.docs[snapshot.docs.length - 1];
        fetchAttempts++;

        // If we got fewer docs than BATCH_SIZE, we've reached the end
        if (snapshot.docs.length < BATCH_SIZE) {
          break;
        }
      }

      // Trim to requested page size
      const finalOrders = orders.slice(0, pageSize);

      // Has more if we filled the page and haven't reached the end
      const hasMore = orders.length >= pageSize && fetchAttempts < MAX_FETCH_ATTEMPTS;

      // Reset error count on success
      this.errorCount = 0;

      logger.info(`[OrderQueries] Fetched ${finalOrders.length} orders for partner ${partnerId.substring(0, 8)} with status ${status} (${totalFetched} docs scanned)`);

      return { orders: finalOrders, lastDoc: currentLastDoc, hasMore };
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('index') || (error as any).code === 'failed-precondition') {
          logger.error('[OrderQueries] Missing Firestore index. Create: (partnerId ASC, status ASC, createdAt DESC)', error);
        }
        this.logError('[OrderQueries] Error fetching orders by partner and status', error);
      } else {
        this.logError('[OrderQueries] Error fetching orders by partner and status', new Error('Unknown error'));
      }
      return { orders: [], lastDoc: null, hasMore: false };
    }
  }
}

export const orderQueries = new OrderQueries();
